import { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import {
  Upload, FileText, CheckCircle2, Loader2, X, File, Image, Table2, ChevronRight
} from 'lucide-react';

const MOCK_FILES = {
  edi_837: [
    { id: 'mock-837-1', name: 'claims_batch_jan2026.x12', size: '2.4 MB', type: 'EDI 837P', icon: FileText },
    { id: 'mock-837-2', name: 'claims_batch_feb2026.x12', size: '1.8 MB', type: 'EDI 837P', icon: FileText },
  ],
  edi_835: [
    { id: 'mock-835-1', name: 'remittance_jan2026.x12', size: '1.1 MB', type: 'EDI 835', icon: FileText },
  ],
  ccda: [
    { id: 'mock-ccda-1', name: 'patient_export_chen.xml', size: '340 KB', type: 'CCDA/CCD', icon: FileText },
    { id: 'mock-ccda-2', name: 'clinic_ccda_batch.zip', size: '12 MB', type: 'CCDA Bundle', icon: File },
  ],
  patient_csv: [
    { id: 'mock-csv-1', name: 'patient_roster_2026.csv', size: '156 KB', type: 'CSV', icon: Table2 },
  ],
  appt_list: [
    { id: 'mock-appt-1', name: 'schedule_mar24_28.csv', size: '48 KB', type: 'CSV', icon: Table2 },
    { id: 'mock-appt-2', name: 'appointments_export.xlsx', size: '92 KB', type: 'Excel', icon: Table2 },
  ],
  patient_balances: [
    { id: 'mock-bal-1', name: 'aging_report_mar2026.csv', size: '210 KB', type: 'CSV', icon: Table2 },
  ],
  fee_schedule: [
    { id: 'mock-fee-1', name: 'fee_schedule_2026.csv', size: '78 KB', type: 'CSV', icon: Table2 },
  ],
  appt_list_echo: [
    { id: 'mock-echo-appt-1', name: 'todays_schedule.csv', size: '22 KB', type: 'CSV', icon: Table2 },
  ],
  calendar_export: [
    { id: 'mock-cal-1', name: 'provider_calendars.ics', size: '64 KB', type: 'ICS', icon: FileText },
  ],
  patient_csv_echo: [
    { id: 'mock-echo-csv-1', name: 'patient_demographics.csv', size: '180 KB', type: 'CSV', icon: Table2 },
  ],
  csv_insurance: [
    { id: 'mock-ins-csv-1', name: 'patient_insurance_list.csv', size: '134 KB', type: 'CSV', icon: Table2 },
  ],
  csv_demographics: [
    { id: 'mock-demo-csv-1', name: 'patient_demographics.csv', size: '198 KB', type: 'CSV', icon: Table2 },
  ],
  csv_practice_report: [
    { id: 'mock-prac-csv-1', name: 'practice_report_q1_2026.csv', size: '256 KB', type: 'CSV', icon: Table2 },
  ],
  csv_physician: [
    { id: 'mock-phys-csv-1', name: 'physician_details.csv', size: '42 KB', type: 'CSV', icon: Table2 },
  ],
  csv_service_location: [
    { id: 'mock-loc-csv-1', name: 'service_locations.csv', size: '18 KB', type: 'CSV', icon: Table2 },
  ],
};

const SetupUploadLightbox = ({ open, onOpenChange, docId, docLabel, onFileSelected }) => {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(false);

  const mockFiles = MOCK_FILES[docId] || [];

  const reset = useCallback(() => { setSelectedFile(null); setProcessing(false); setDone(false); setDragOver(false); }, []);

  const simulateUpload = useCallback((file) => {
    setSelectedFile(file);
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      setDone(true);
      setTimeout(() => {
        onFileSelected?.(file);
        reset();
        onOpenChange(false);
      }, 800);
    }, 1800);
  }, [onFileSelected, onOpenChange, reset]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setDragOver(true); }, []);
  const handleDragLeave = useCallback(() => setDragOver(false), []);
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) simulateUpload({ id: `real-${Date.now()}`, name: file.name, size: `${(file.size / 1024).toFixed(0)} KB`, type: file.type || 'File', icon: File });
  }, [simulateUpload]);
  const handleClose = () => { reset(); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden" style={{ maxHeight: '80vh' }} aria-describedby={undefined} data-testid="setup-upload-lightbox">
        <div style={{ padding: '16px 20px', borderBottom: '0.5px solid #e8e8e8' }}>
          <DialogTitle style={{ fontSize: 16, fontWeight: 500, color: '#111111', margin: 0 }}>
            {done ? 'File processed' : processing ? 'Processing...' : `Upload ${docLabel}`}
          </DialogTitle>
          {!processing && !done && <p style={{ fontSize: 12, color: '#888888', marginTop: 2 }}>Drag and drop a file, select from your computer, or choose a sample file below</p>}
        </div>

        <div style={{ padding: '16px 20px', maxHeight: '55vh', overflowY: 'auto' }}>
          {/* Processing state */}
          {processing && selectedFile && (
            <div style={{ textAlign: 'center', padding: '32px 0' }} data-testid="upload-processing">
              <Loader2 style={{ width: 36, height: 36, color: '#111111', margin: '0 auto 12px', animation: 'spin 1.5s linear infinite' }} />
              <p style={{ fontSize: 14, fontWeight: 500, color: '#111111', margin: '0 0 4px' }}>Processing with Lens...</p>
              <p style={{ fontSize: 12, color: '#888888' }}>{selectedFile.name}</p>
              <p style={{ fontSize: 11, color: '#aaaaaa', marginTop: 8 }}>Extracting data and validating format</p>
            </div>
          )}

          {/* Done state */}
          {done && selectedFile && (
            <div style={{ textAlign: 'center', padding: '32px 0' }} data-testid="upload-done">
              <CheckCircle2 style={{ width: 36, height: 36, color: '#22c55e', margin: '0 auto 10px' }} />
              <p style={{ fontSize: 15, fontWeight: 500, color: '#111111', margin: '0 0 4px' }}>File processed successfully</p>
              <p style={{ fontSize: 12, color: '#888888' }}>{selectedFile.name}</p>
            </div>
          )}

          {/* Upload UI */}
          {!processing && !done && (
            <>
              {/* Drag and drop zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  background: dragOver ? '#f0f0ff' : '#fafafa',
                  border: `2px dashed ${dragOver ? '#6b5ce7' : '#d1d5db'}`,
                  borderRadius: 12, padding: '28px 16px', textAlign: 'center',
                  transition: 'all 150ms', cursor: 'pointer',
                }}
                onClick={() => document.getElementById(`file-input-${docId}`)?.click()}
                data-testid="upload-dropzone"
              >
                <Upload style={{ width: 24, height: 24, color: dragOver ? '#6b5ce7' : '#888888', margin: '0 auto 8px' }} />
                <p style={{ fontSize: 13, fontWeight: 500, color: '#111111', margin: '0 0 4px' }}>
                  {dragOver ? 'Drop your file here' : 'Drag and drop your file here'}
                </p>
                <p style={{ fontSize: 11, color: '#aaaaaa', margin: '0 0 10px' }}>or click to browse your computer</p>
                <button
                  style={{ background: '#111111', color: '#ffffff', borderRadius: 8, height: 32, padding: '0 16px', fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer' }}
                  data-testid="upload-browse-btn"
                >
                  Browse files
                </button>
                <input
                  id={`file-input-${docId}`}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) simulateUpload({ id: `real-${Date.now()}`, name: file.name, size: `${(file.size / 1024).toFixed(0)} KB`, type: file.type || 'File', icon: File });
                  }}
                  data-testid="upload-file-input"
                />
              </div>

              {/* Sample files */}
              {mockFiles.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <p style={{ fontSize: 10, color: '#aaaaaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                    Or choose a sample file
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {mockFiles.map(file => {
                      const Icon = file.icon;
                      return (
                        <button
                          key={file.id}
                          onClick={() => simulateUpload(file)}
                          className="flex items-center text-left"
                          style={{ padding: '10px 12px', borderRadius: 8, border: '0.5px solid #e8e8e8', background: '#ffffff', cursor: 'pointer', gap: 10, transition: 'border-color 150ms, background 100ms' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#111111'; e.currentTarget.style.background = '#fafafa'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e8e8'; e.currentTarget.style.background = '#ffffff'; }}
                          data-testid={`sample-file-${file.id}`}
                        >
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Icon style={{ width: 16, height: 16, color: '#555555' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 12, fontWeight: 500, color: '#111111', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</p>
                            <p style={{ fontSize: 10, color: '#aaaaaa', margin: '1px 0 0' }}>{file.size} · {file.type}</p>
                          </div>
                          <ChevronRight style={{ width: 14, height: 14, color: '#cccccc', flexShrink: 0 }} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!processing && !done && (
          <div style={{ padding: '12px 20px', borderTop: '0.5px solid #e8e8e8' }}>
            <button
              onClick={handleClose}
              style={{ width: '100%', height: 36, borderRadius: 8, background: '#ffffff', color: '#555555', fontSize: 12, border: '0.5px solid #e0e0e0', cursor: 'pointer' }}
              data-testid="upload-cancel-btn"
            >
              Cancel
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SetupUploadLightbox;
